import { api } from "./api";
import type {
  MemorySearchRequest,
  MemorySearchResponse,
  SignalStateHashResponse,
} from "@/types";

/**
 * Experiment memory search service adapter (R9 / Sub-3).
 *
 * Wraps `POST /api/v1/memory/search` (Sub-2 endpoint, persistence
 * backed by Sub-1's `experiment_memory_search_index` table). Stateless;
 * no Idempotency-Key required -- the read path is pure retrieval.
 *
 * Source:
 * - docs/engineering/TASKS.md § ENG-R9-MEMORY-SEARCH Sub-3;
 * - docs/engineering/contracts/HTTP_API.md `POST /api/v1/memory/search`;
 * - backend/app/api/v1/memory_search.py (canonical handler).
 */
export async function memorySearch(
  payload: MemorySearchRequest,
): Promise<MemorySearchResponse> {
  const { data } = await api.post<MemorySearchResponse>(
    "/api/v1/memory/search",
    payload,
  );
  return data;
}

/**
 * Per-app signal_state_hash reader. Wraps
 * `GET /api/v1/memory/signal-state-hash/{app_id}` (HTTP_API.md).
 * Used by `useMemorySearch` to populate
 * `MemorySearchRequest.signal_state_hash` with a real cache-coherent
 * value -- replaces the `MEMORY_SEARCH_SIGNAL_STATE_PLACEHOLDER`
 * literal that R9-Sub-3 shipped with.
 *
 * The endpoint never returns 404 -- on first-run apps it returns a
 * deterministic synthetic hash with `bootstrap=true`. Callers MUST
 * trust the returned string and pass it through verbatim.
 */
export async function signalStateHash(
  appId: string,
): Promise<SignalStateHashResponse> {
  const { data } = await api.get<SignalStateHashResponse>(
    `/api/v1/memory/signal-state-hash/${encodeURIComponent(appId)}`,
  );
  return data;
}
